import { useContext, useEffect, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { useNavigate } from 'react-router-dom'
import robot from '../../../assets/robot.png'
import { ToastContext } from '../../../context/ToastContext'
import AudioRecorder from '../views/contableAgent/Chat/AudioRecorder'
import DefaultQuestions from '../views/contableAgent/Chat/DefaultQuestions'
import Messages from '../views/contableAgent/Chat/Messages'
import { useTryLucaChat } from '../views/contableAgent/context/TryLucaChatContext'

const AssistantPetChatPanel = ({ onClose }) => {
  const {
    isLoading,
    limitExceeded,
    messages,
    sendMessage
  } = useTryLucaChat()
  const [newMessage, setNewMessage] = useState('')
  const [showQuestions, setShowQuestions] = useState(true)
  const [areQuestionsDismissed, setAreQuestionsDismissed] = useState(false)
  const [questionKey, setQuestionKey] = useState(0)
  const messagesContainerRef = useRef(null)
  const questionsRef = useRef(null)
  const navigate = useNavigate()
  const { showToast } = useContext(ToastContext)

  useEffect(() => {
    if (messages.length === 0) {
      setShowQuestions(true)
      setQuestionKey(0)
    } else if (!isLoading) {
      setShowQuestions(true)
      setQuestionKey(previousKey => previousKey + 1)
    }
  }, [isLoading, messages.length])

  const handleSendMessage = async message => {
    if (message.trim() === '') return

    setShowQuestions(false)
    setNewMessage('')
    await sendMessage(message)
  }

  const handleKeyDown = event => {
    if (isLoading) return

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage(newMessage)
    }
  }

  const handleTranscriptionComplete = async transcription => {
    if (transcription && transcription.trim()) {
      setNewMessage(transcription)

      try {
        await handleSendMessage(transcription)
      } catch {
        showToast('error', 'Error al enviar el mensaje transcrito')
      }
    }
  }

  const areQuickSuggestionsVisible = showQuestions && !areQuestionsDismissed
  const isInitialChatState = messages.length === 0 && !isLoading

  return (
    <section className='pointer-events-auto w-[min(calc(100vw-1.5rem),24rem)] overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.18)]'>
      <div className='flex items-center gap-2.5 bg-primary px-3.5 py-2.5 text-white'>
        <div className='flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.3)]'>
          <img src={robot} alt='LUCA' className='h-5 w-5 object-contain' draggable={false} />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-semibold tracking-[0.06em]'>TryLuca (AI)</div>
        </div>
        <button
          type='button'
          onClick={() => navigate('/chat?tab=chat')}
          className='inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white'
          aria-label='Abrir chat completo'
          title='Abrir chat completo'
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 17 }} />
        </button>
        <button
          type='button'
          onClick={onClose}
          className='inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white'
          aria-label='Cerrar chat de LUCA'
          title='Cerrar'
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div
        ref={messagesContainerRef}
        className={`max-h-[min(45vh,22rem)] min-h-[10rem] overflow-y-auto bg-gradient-to-b from-white via-white to-slate-50 px-2 py-2 ${
          isInitialChatState ? 'sm:py-3' : ''
        }`}
      >
        <div
          className={`flex min-h-full flex-col space-y-2 ${
            isInitialChatState ? 'justify-end' : areQuickSuggestionsVisible ? 'pb-2' : ''
          }`}
        >
          <Messages messages={messages} isLoading={isLoading} />
        </div>
      </div>

      <div className='border-t border-slate-200/80 bg-slate-50/90 px-3 py-2'>
        <DefaultQuestions
          handleSendMessage={handleSendMessage}
          onClose={() => setAreQuestionsDismissed(true)}
          showQuestions={areQuickSuggestionsVisible}
          questionKey={questionKey}
          questionsRef={questionsRef}
        />

        <div
          className={`overflow-hidden rounded-[18px] border border-accent2/20 bg-white shadow-[0_14px_28px_rgba(15,23,42,0.06)] ${
            areQuickSuggestionsVisible ? 'mt-2' : 'mt-0'
          }`}
        >
          {limitExceeded ? (
            <div className='border-b border-danger/10 bg-danger/5 px-3 py-1.5 text-xs font-medium text-danger'>
              Sin tokens disponibles por ahora.
            </div>
          ) : null}
          <div className='flex items-center gap-2 px-3 py-2'>
            <input
              type='text'
              value={newMessage}
              onChange={event => setNewMessage(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={limitExceeded ? 'Límite alcanzado para este plan' : 'Escribe tu mensaje'}
              disabled={limitExceeded}
              className='h-8 min-w-0 flex-1 border-none bg-transparent text-sm text-text placeholder:text-muted focus:outline-none focus:ring-0'
            />
            <AudioRecorder
              onTranscriptionComplete={handleTranscriptionComplete}
              onError={errorMessage => showToast('error', errorMessage)}
              disabled={limitExceeded || isLoading}
              variant='inlineComposerLight'
            />
            <button
              type='button'
              onClick={() => handleSendMessage(newMessage)}
              disabled={limitExceeded || isLoading || newMessage.trim() === ''}
              className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-[0_10px_24px_rgba(15,23,42,0.16)] transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40'
              aria-label='Enviar mensaje'
              title='Enviar mensaje'
            >
              <SendRoundedIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AssistantPetChatPanel
